import { useContext, useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Tooltip from "@mui/material/Tooltip";
import moment from "moment-timezone";
import dayjs from "dayjs";

import ThemeContext from "../lib/theme-context";

import EnhancedImageLightbox from "../components/EnhancedImageLightbox";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import useWindowDimensions from "../utils/width";

const QUESTION_CUTOFF_DATE = new Date('2026-05-17T23:59:59');

const getJournalQuestions = (isNewQuestions) => {
  if (isNewQuestions) {
    return {
      question_1: "Did I strictly follow my trading plan? Where did I deviate?",
      question_2: "If this exact same setup appears tomorrow, is it system-appropriate to take again, and why?",
      question_3: "What specific emotions did I battle before entry, while active, and after exit?",
      question_4: "What is the single biggest lesson this trade highlighted that I need to remember for the future?",
    };
  } else {
    return {
      question_1: "What were the pros ?",
      question_2: "What were the cons ?",
      question_3: "What emotions did I feel ?",
      question_4: "Would I take this trade again ?",
      question_5: "Did I follow my plan ?",
      question_6: "Anything to add ?",
    };
  }
};

const imgStyle = {
  width: "100%",
  height: "430px",
  objectFit: "cover",
  borderRadius: "16px",
  cursor: "pointer",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
};

const SharedJournal = () => {
  const { width } = useWindowDimensions();
  const theme = useContext(ThemeContext);

  const [journal, setJournal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [accordion, setAccordion] = useState(false);
  
  const [pair, setPair] = useState("");
  const [direction, setDirection] = useState("");
  const [date, setDate] = useState(dayjs(new Date()));
  const [entryTime, setEntryTime] = useState("");
  const [entryTimeframe, setEntryTimeframe] = useState("");
  const [exitDate, setExitDate] = useState("");
  const [exitTime, setExitTime] = useState("");
  const [useNewQuestions, setUseNewQuestions] = useState(true);
  const [positioning, setPositioning] = useState("");
  const [sequence, setSequence] = useState("");
  const [forecastGrade, setForecastGrade] = useState("");
  const [tradeGrade, setTradeGrade] = useState("");
  const [equityType, setEquityType] = useState("");
  const [outcomePC, setOutcomePC] = useState("");
  const [outcomeIC, setOutcomeIC] = useState("");
  const [profitLossPercentagePC, setProfitLossPercentagePC] = useState(0);
  const [profitLossPercentageIC, setProfitLossPercentageIC] = useState(0);
  const [percentWithFeesPC, setPercentWithFeesPC] = useState(0);
  const [percentWithFeesIC, setPercentWithFeesIC] = useState(0);
  
  const [pros, setPros] = useState("");
  const [cons, setCons] = useState("");
  const [emotions, setEmotions] = useState("");
  const [plan, setPlan] = useState("");
  const [takeAgain, setTakeAgain] = useState("");
  const [extraNotes, setExtraNotes] = useState("");
  // New question sub-sections
  const [executionRight, setExecutionRight] = useState("");
  const [executionDeviate, setExecutionDeviate] = useState("");
  const [systemAppropriate, setSystemAppropriate] = useState("");
  const [systemWhy, setSystemWhy] = useState("");
  const [emotionsBefore, setEmotionsBefore] = useState("");
  const [emotionsWhile, setEmotionsWhile] = useState("");
  const [emotionsAfter, setEmotionsAfter] = useState("");

  const [min5_before, setMin5_before] = useState("");
  const [min5_after, setMin5_after] = useState("");
  const [min15_before, setMin15_before] = useState("");
  const [min15_after, setMin15_after] = useState("");
  const [hour1_before, setHour1_before] = useState("");
  const [hour1_after, setHour1_after] = useState("");
  const [hour4_before, setHour4_before] = useState("");
  const [hour4_after, setHour4_after] = useState("");
  const [daily_before, setDaily_before] = useState("");
  const [daily_after, setDaily_after] = useState("");
  const [weekly_before, setWeekly_before] = useState("");
  const [weekly_after, setWeekly_after] = useState("");
  const [dxyHourlyBefore, setDxyHourlyBefore] = useState("");
  const [dxyHourlyAfter, setDxyHourlyAfter] = useState("");
  const [dxyDailyBefore, setDxyDailyBefore] = useState("");
  const [dxyDailyAfter, setDxyDailyAfter] = useState("");

  const [min5_beforeNote, setMin5_beforeNote] = useState("");
  const [min5_afterNote, setMin5_afterNote] = useState("");
  const [min15_beforeNote, setMin15_beforeNote] = useState("");
  const [min15_afterNote, setMin15_afterNote] = useState("");
  const [oneHour_beforeNote, setOneHour_beforeNote] = useState("");
  const [oneHour_afterNote, setOneHour_afterNote] = useState("");
  const [fourHour_beforeNote, setFourHour_beforeNote] = useState("");
  const [fourHour_afterNote, setFourHour_afterNote] = useState("");
  const [daily_beforeNote, setDaily_beforeNote] = useState("");
  const [daily_afterNote, setDaily_afterNote] = useState("");
  const [weekly_beforeNote, setWeekly_beforeNote] = useState("");
  const [weekly_afterNote, setWeekly_afterNote] = useState("");
  const [dxyHourlyBeforeNote, setDxyHourlyBeforeNote] = useState("");
  const [dxyHourlyAfterNote, setDxyHourlyAfterNote] = useState("");
  const [dxyDailyBeforeNote, setDxyDailyBeforeNote] = useState("");
  const [dxyDailyAfterNote, setDxyDailyAfterNote] = useState("");

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  useEffect(() => {
    const fetchSharedJournal = async () => {
      try {
        const pathParts = window.location.pathname.split('/');
        
        const pair = pathParts[2];
        const direction = pathParts[3];
        const dateSlug = pathParts[4];
        
        const apiUrl = `${process.env.REACT_APP_API}/shared-journal/${pair}/${direction}/${dateSlug}`;
        
        const response = await fetch(apiUrl);
        
        if (response.ok) {
          const data = await response.json();
          
                    
          setJournal(data);
          
          setPair(data.pair);
          setDirection(data.direction);
          setDate(data.date);
          setEntryTime(data.entryTime);
          setEntryTimeframe(data.entryTimeframe);
          setExitDate(data.exitDate);
          setExitTime(data.exitTime);
          setPositioning(data.positioning);
          setSequence(data.sequence);
          setForecastGrade(data.forecastGrade);
          setTradeGrade(data.tradeGrade);
          setEquityType(data.equityType);
          setOutcomePC(data.outcomePC);
          setOutcomeIC(data.outcomeIC);
          setProfitLossPercentagePC(data.profitLossPercentagePC || 0);
          setProfitLossPercentageIC(data.profitLossPercentageIC || 0);
          setPercentWithFeesPC(data.percentWithFeesPC || 0);
          setPercentWithFeesIC(data.percentWithFeesIC || 0);
          
          const journalDate = new Date(data.date);
          const shouldUseNewQuestions = journalDate > QUESTION_CUTOFF_DATE;
          setUseNewQuestions(shouldUseNewQuestions);
          
          setPros(data.pros);
          setCons(data.cons);
          setEmotions(data.emotions);
          setPlan(data.plan);
          setTakeAgain(data.takeAgain);
          setExtraNotes(data.extraNotes);
          setExecutionRight(data.executionRight || "");
          setExecutionDeviate(data.executionDeviate || "");
          setSystemAppropriate(data.systemAppropriate || "");
          setSystemWhy(data.systemWhy || "");
          setEmotionsBefore(data.emotionsBefore || "");
          setEmotionsWhile(data.emotionsWhile || "");
          setEmotionsAfter(data.emotionsAfter || "");
          
          setMin5_before(data.min5_before);
          setMin5_after(data.min5_after);
          setMin15_before(data.min15_before);
          setMin15_after(data.min15_after);
          setHour1_before(data.hour1_before);
          setHour1_after(data.hour1_after);
          setHour4_before(data.hour4_before);
          setHour4_after(data.hour4_after);
          setDaily_before(data.daily_before);
          setDaily_after(data.daily_after);
          setWeekly_before(data.weekly_before);
          setWeekly_after(data.weekly_after);
          setDxyHourlyBefore(data.dxyHourlyBefore);
          setDxyHourlyAfter(data.dxyHourlyAfter);
          setDxyDailyBefore(data.dxyDailyBefore);
          setDxyDailyAfter(data.dxyDailyAfter);
          
          setMin5_beforeNote(data.min5_beforeNote);
          setMin5_afterNote(data.min5_afterNote);
          setMin15_beforeNote(data.min15_beforeNote);
          setMin15_afterNote(data.min15_afterNote);
          setOneHour_beforeNote(data.oneHour_beforeNote);
          setOneHour_afterNote(data.oneHour_afterNote);
          setFourHour_beforeNote(data.fourHour_beforeNote);
          setFourHour_afterNote(data.fourHour_afterNote);
          setDaily_beforeNote(data.daily_beforeNote);
          setDaily_afterNote(data.daily_afterNote);
          setWeekly_beforeNote(data.weekly_beforeNote);
          setWeekly_afterNote(data.weekly_afterNote);
          setDxyHourlyBeforeNote(data.dxyHourlyBeforeNote);
          setDxyHourlyAfterNote(data.dxyHourlyAfterNote);
          setDxyDailyBeforeNote(data.dxyDailyBeforeNote);
          setDxyDailyAfterNote(data.dxyDailyAfterNote);
        } else {
          const errorMessage = await response.text();
          if (response.status === 404) {
            setError("Journal not found");
          } else {
            setError(errorMessage || "Error fetching journal");
          }
        }
      } catch (err) {
        console.error("Error fetching shared journal:", err);
        setError("Error fetching journal");
      } finally {
        setLoading(false);
      }
    };

    fetchSharedJournal();
  }, []);

  const allUrls = [
    min5_before,
    min5_after,
    min15_before,
    min15_after,
    hour1_before,
    hour1_after,
    hour4_before,
    hour4_after,
    daily_before,
    daily_after,
    weekly_before,
    weekly_after,
    dxyHourlyBefore,
    dxyHourlyAfter,
    dxyDailyBefore,
    dxyDailyAfter,
  ];
  
  const urls = allUrls.filter(url => url && (url.startsWith("https://") || url.startsWith("http://")));
  
  const allTitles = [
    "5m Before",
    "5m After",
    "15m Before",
    "15m After",
    "1h Before",
    "1h After",
    "4h Before",
    "4h After",
    "Daily Before",
    "Daily After",
    "Weekly Before",
    "Weekly After",
    "DXY LTF Before",
    "DXY LTF After",
    "DXY HTF Before",
    "DXY HTF After",
  ];
  
  const labels = allUrls
    .map((url, index) => {
      if (!url || (!url.startsWith("https://") && !url.startsWith("http://"))) {
        return null;
      }
      
      let isEntryTimeframe = false;
      if (entryTimeframe) {
        const entryTF = entryTimeframe;
        switch (index) {
          case 0:
          case 1:
            isEntryTimeframe = entryTF === "5m";
            break;
          case 2:
          case 3:
            isEntryTimeframe = entryTF === "15m";
            break;
          case 4:
          case 5:
            isEntryTimeframe = entryTF === "1H";
            break;
          case 6:
          case 7:
            isEntryTimeframe = entryTF === "4H";
            break;
          case 8:
          case 9:
            isEntryTimeframe = entryTF === "D";
            break;
          case 10:
          case 11:
            isEntryTimeframe = entryTF === "W";
            break;
          default:
            isEntryTimeframe = false;
        }
      }
      
      return { title: allTitles[index], isEntryTimeframe };
    })
    .filter(label => label !== null);

  useEffect(() => {
    document.onkeydown = checkKey;
    function checkKey(e) {
      if (e.keyCode === 39 || e.keyCode === 37 || e.keyCode === 76 || e.keyCode === 72) {
        closeImageViewer(0);
      }
    }
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
        }}
      >
        <div style={{ color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e" }}>
          Loading journal...
        </div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
        }}
      >
        <div style={{ 
          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
          textAlign: "center",
          padding: "40px",
          maxWidth: "500px"
        }}>
          <ErrorOutlineIcon 
            sx={{ 
              fontSize: "80px", 
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              marginBottom: "20px"
            }} 
          />
          <h2 style={{ 
            fontSize: "24px", 
            fontWeight: "600",
            marginBottom: "12px",
            color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e"
          }}>
            Journal Not Found
          </h2>
          <p style={{ 
            fontSize: "16px",
            lineHeight: "1.6",
            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280"
          }}>
            This journal may not exist or has been deactivated.
          </p>
        </div>
      </Box>
    );
  }

  if (!journal) {
    return null;
  }

  const questions = getJournalQuestions(useNewQuestions);

  return (
    <Box
      sx={{
        overflowY: !isViewerOpen ? "scroll" : null,
        overflowX: !isViewerOpen ? "hidden" : null,
        position: !isViewerOpen ? "absolute" : "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
        width: "100%",
        maxWidth: "1920px",
        height: "100%",
        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
        padding: "0px 15px 15px 15px",
        margin: "0 auto",
      }}
    >
      {isViewerOpen && (
        <EnhancedImageLightbox
          images={urls}
          labels={labels}
          currentIndex={currentImage}
          onClose={closeImageViewer}
          onNavigate={setCurrentImage}
        />
      )}

      <Grid
        container
        justifyContent="space-evenly"
        alignItems="center"
        sx={{ minHeight: "100%" }}
      >
        <Grid
          item
          xs={12}
          sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
        >
          <div
            style={{
              maxWidth: "95%",
              margin: width <= 768 ? "0.5em auto" : "1em auto 0.5em auto",
              marginBottom: width <= 768 ? "2.5em" : "2em",
              padding: width <= 768 ? "16px 20px" : "20px 32px",
              borderRadius: "16px",
            }}
          >
            <div
              style={{
                padding: "16px",
                margin: "16px 0",
                borderRadius: "12px",
                backgroundColor:
                  theme[0] === "dark"
                    ? "rgba(45, 46, 51, 0.4)"
                    : "rgba(249, 250, 251, 0.8)",
                border: `1px solid ${
                  theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                }`,
                boxShadow:
                  theme[0] === "dark"
                    ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                    : "0 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              {width <= 768 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {pair && (
                    <span
                      style={{
                        fontSize: "26px",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                        color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                      }}
                    >
                      {pair}
                    </span>
                  )}
                  {direction && (
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                        padding: "3px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          direction === "Short"
                            ? theme[0] === "dark"
                              ? "rgba(219, 6, 113, 0.15)"
                              : "rgba(219, 6, 113, 0.1)"
                            : direction === "Long"
                              ? theme[0] === "dark"
                                ? "rgba(38, 166, 93, 0.15)"
                                : "rgba(38, 166, 93, 0.1)"
                              : theme[0] === "dark"
                              ? "rgba(237, 158, 14, 0.15)"
                              : "rgba(237, 158, 14, 0.1)",
                        border: `2px solid ${
                          direction === "Short"
                            ? "#db0671"
                            : direction === "Long"
                              ? "#26a65d"
                              : "#ED9E0E"
                        }`,
                        color:
                          direction === "Short"
                            ? "#db0671"
                            : direction === "Long"
                              ? "#26a65d"
                              : "#ED9E0E",
                      }}
                    >
                      {direction}
                    </span>
                  )}
                  {(equityType === "Personal Capital" || equityType === "Backtest") && outcomePC && (
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                        padding: "3px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          outcomePC === "Loss"
                            ? theme[0] === "dark"
                              ? "rgba(242, 54, 69, 0.15)"
                              : "rgba(242, 54, 69, 0.1)"
                            : outcomePC === "Win"
                              ? theme[0] === "dark"
                                ? "rgba(38, 166, 93, 0.15)"
                                : "rgba(38, 166, 93, 0.1)"
                              : theme[0] === "dark"
                                ? "rgba(237, 158, 14, 0.15)"
                                : "rgba(237, 158, 14, 0.1)",
                        border: `2px solid ${
                          outcomePC === "Loss"
                            ? "#f23645"
                            : outcomePC === "Win"
                              ? "#26a65d"
                              : "#ED9E0E"
                        }`,
                        color:
                          outcomePC === "Loss"
                            ? "#f23645"
                            : outcomePC === "Win"
                              ? "#26a65d"
                              : "#ED9E0E",
                      }}
                    >
                      {outcomePC}
                    </span>
                  )}
                  {equityType === "Investor Capital" && outcomeIC && (
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                        padding: "3px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          outcomeIC === "Loss"
                            ? theme[0] === "dark"
                              ? "rgba(242, 54, 69, 0.15)"
                              : "rgba(242, 54, 69, 0.1)"
                            : outcomeIC === "Win"
                              ? theme[0] === "dark"
                                ? "rgba(38, 166, 93, 0.15)"
                                : "rgba(38, 166, 93, 0.1)"
                              : theme[0] === "dark"
                                ? "rgba(237, 158, 14, 0.15)"
                                : "rgba(237, 158, 14, 0.1)",
                        border: `2px solid ${
                          outcomeIC === "Loss"
                            ? "#f23645"
                            : outcomeIC === "Win"
                              ? "#26a65d"
                              : "#ED9E0E"
                        }`,
                        color:
                          outcomeIC === "Loss"
                            ? "#f23645"
                            : outcomeIC === "Win"
                              ? "#26a65d"
                              : "#ED9E0E",
                      }}
                    >
                      {outcomeIC}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: "18px",
                      fontWeight: "400",
                      letterSpacing: "0.3px",
                      color:
                        theme[0] === "dark"
                          ? "rgba(252, 252, 255, 0.7)"
                          : "rgba(26, 27, 30, 0.7)",
                    }}
                  >
                    {date && moment(date, ["DD/MM/YY", "YYYY-MM-DD", moment.ISO_8601], true).format("ddd Do MMM YYYY")}
                  </span>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  {pair && (
                    <span
                      style={{
                        fontSize: "23px",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                        color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                      }}
                    >
                      {pair}
                    </span>
                  )}

                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      letterSpacing: "0.5px",
                      padding: "3px 8px",
                      borderRadius: "6px",
                      backgroundColor:
                        direction === "Short"
                          ? theme[0] === "dark"
                            ? "rgba(219, 6, 113, 0.15)"
                            : "rgba(219, 6, 113, 0.1)"
                          : direction === "Long"
                            ? theme[0] === "dark"
                              ? "rgba(38, 166, 93, 0.15)"
                              : "rgba(38, 166, 93, 0.1)"
                            : theme[0] === "dark"
                            ? "rgba(237, 158, 14, 0.15)"
                            : "rgba(237, 158, 14, 0.1)",
                      border: `2px solid ${
                        direction === "Short"
                          ? "#db0671"
                          : direction === "Long"
                            ? "#26a65d"
                            : "#ED9E0E"
                      }`,
                      color:
                        direction === "Short"
                          ? "#db0671"
                          : direction === "Long"
                            ? "#26a65d"
                            : "#ED9E0E",
                    }}
                  >
                    {direction}
                  </span>

                  {(equityType === "Personal Capital" || equityType === "Backtest") && outcomePC && (
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                        padding: "3px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          outcomePC === "Loss"
                            ? theme[0] === "dark"
                              ? "rgba(242, 54, 69, 0.15)"
                              : "rgba(242, 54, 69, 0.1)"
                            : outcomePC === "Win"
                              ? theme[0] === "dark"
                                ? "rgba(38, 166, 93, 0.15)"
                                : "rgba(38, 166, 93, 0.1)"
                              : theme[0] === "dark"
                                ? "rgba(237, 158, 14, 0.15)"
                                : "rgba(237, 158, 14, 0.1)",
                        border: `2px solid ${
                          outcomePC === "Loss"
                            ? "#f23645"
                            : outcomePC === "Win"
                              ? "#26a65d"
                              : "#ED9E0E"
                        }`,
                        color:
                          outcomePC === "Loss"
                            ? "#f23645"
                            : outcomePC === "Win"
                              ? "#26a65d"
                              : "#ED9E0E",
                      }}
                    >
                      {outcomePC}
                    </span>
                  )}

                  {equityType === "Investor Capital" && outcomeIC && (
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                        padding: "3px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          outcomeIC === "Loss"
                            ? theme[0] === "dark"
                              ? "rgba(242, 54, 69, 0.15)"
                              : "rgba(242, 54, 69, 0.1)"
                            : outcomeIC === "Win"
                              ? theme[0] === "dark"
                                ? "rgba(38, 166, 93, 0.15)"
                                : "rgba(38, 166, 93, 0.1)"
                              : theme[0] === "dark"
                                ? "rgba(237, 158, 14, 0.15)"
                                : "rgba(237, 158, 14, 0.1)",
                        border: `2px solid ${
                          outcomeIC === "Loss"
                            ? "#f23645"
                            : outcomeIC === "Win"
                              ? "#26a65d"
                              : "#ED9E0E"
                        }`,
                        color:
                          outcomeIC === "Loss"
                            ? "#f23645"
                            : outcomeIC === "Win"
                              ? "#26a65d"
                              : "#ED9E0E",
                      }}
                    >
                      {outcomeIC}
                    </span>
                  )}

                  <span
                    style={{
                      fontSize: "18px",
                      fontWeight: "400",
                      letterSpacing: "0.3px",
                      color:
                        theme[0] === "dark"
                          ? "rgba(252, 252, 255, 0.7)"
                          : "rgba(26, 27, 30, 0.7)",
                    }}
                  >
                    {date && moment(date, ["DD/MM/YY", "YYYY-MM-DD", moment.ISO_8601], true).format("ddd Do MMM YYYY")}
                  </span>
                </div>
              )}

              <hr
                style={{
                  width: "100%",
                  border: "none",
                  borderTop: `1px solid ${
                    theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                  }`,
                  margin: "16px 0",
                }}
              />

              {(outcomePC || positioning || sequence || tradeGrade || forecastGrade) && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent:
                      width < 768 ? "center" : "space-evenly",
                    alignItems: "center",
                    gap: width < 768 ? "8px" : "12px",
                    flexWrap: "wrap",
                    marginTop: width <= 768 ? "12px" : "16px",
                    maxWidth: "100%",
                  }}
                >
                  {(() => {
                    const usePC = equityType === "Personal Capital" || equityType === "Backtest";
                    const grossPercent = usePC ? profitLossPercentagePC : profitLossPercentageIC;
                    const netPercent = usePC ? percentWithFeesPC : percentWithFeesIC;
                    
                                        
                    return (
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "center" }}>
                        {/* GROSS Badge */}
                        {(grossPercent !== undefined && grossPercent !== null && !isNaN(grossPercent)) && (
                          <div
                            style={{
                              position: "relative",
                              padding: "4px 10px",
                              borderRadius: "12px",
                              backgroundColor: grossPercent > 0
                                ? theme[0] === "dark" ? "rgba(38, 166, 93, 0.15)" : "rgba(38, 166, 93, 0.1)"
                                : grossPercent < 0
                                ? theme[0] === "dark" ? "rgba(242, 54, 69, 0.15)" : "rgba(242, 54, 69, 0.1)"
                                : theme[0] === "dark" ? "rgba(237, 158, 14, 0.15)" : "rgba(237, 158, 14, 0.1)",
                              border: `2px solid ${
                                grossPercent > 0
                                  ? "#26a65d"
                                  : grossPercent < 0
                                  ? "#f23645"
                                  : "#ED9E0E"
                              }`,
                              minWidth: width < 768 ? "60px" : "70px",
                                maxWidth: "120px",
                              textAlign: "center",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "8px",
                                fontWeight: "600",
                                letterSpacing: "0.8px",
                                color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                                margin: "0 0 2px 0",
                              }}
                            >
                              GROSS
                            </p>
                            <p
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                letterSpacing: "0.3px",
                                margin: 0,
                                color: grossPercent > 0
                                  ? "#26a65d"
                                  : grossPercent < 0
                                  ? "#f23645"
                                  : "#ED9E0E",
                              }}
                            >
                              {grossPercent !== 0
                                ? `${grossPercent > 0 ? "+" : ""}${Math.abs(parseFloat(grossPercent)).toFixed(2)}%`
                                : "0%"}
                            </p>
                          </div>
                        )}
                        
                        {/* NET Badge */}
                        {(netPercent !== undefined && netPercent !== null && !isNaN(netPercent)) && (
                          <div
                            style={{
                              position: "relative",
                              padding: "4px 10px",
                              borderRadius: "12px",
                              backgroundColor: netPercent > 0
                                ? theme[0] === "dark" ? "rgba(38, 166, 93, 0.15)" : "rgba(38, 166, 93, 0.1)"
                                : netPercent < 0
                                ? theme[0] === "dark" ? "rgba(242, 54, 69, 0.15)" : "rgba(242, 54, 69, 0.1)"
                                : theme[0] === "dark" ? "rgba(237, 158, 14, 0.15)" : "rgba(237, 158, 14, 0.1)",
                              border: `2px solid ${
                                netPercent > 0
                                  ? "#26a65d"
                                  : netPercent < 0
                                  ? "#f23645"
                                  : "#ED9E0E"
                              }`,
                              minWidth: width < 768 ? "60px" : "70px",
                                maxWidth: "120px",
                              textAlign: "center",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "8px",
                                fontWeight: "600",
                                letterSpacing: "0.8px",
                                color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                                margin: "0 0 2px 0",
                              }}
                            >
                              NET
                            </p>
                            <p
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                letterSpacing: "0.3px",
                                margin: 0,
                                color: netPercent > 0
                                  ? "#26a65d"
                                  : netPercent < 0
                                  ? "#f23645"
                                  : "#ED9E0E",
                              }}
                            >
                              {netPercent !== 0
                                ? `${netPercent > 0 ? "+" : ""}${Math.abs(parseFloat(netPercent)).toFixed(2)}%`
                                : "0%"}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {(positioning || sequence) && (
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "center" }}>
                      {positioning && (
                        <div
                          style={{
                            position: "relative",
                            padding: "4px 10px",
                            borderRadius: "12px",
                            backgroundColor:
                              positioning === "Excellent Positioning"
                                ? theme[0] === "dark"
                                  ? "rgba(38, 166, 93, 0.15)"
                                  : "rgba(38, 166, 93, 0.1)"
                                : positioning === "Average Positioning"
                                ? theme[0] === "dark"
                                  ? "rgba(237, 158, 14, 0.15)"
                                  : "rgba(237, 158, 14, 0.1)"
                                : theme[0] === "dark"
                                ? "rgba(242, 54, 69, 0.15)"
                                : "rgba(242, 54, 69, 0.1)",
                            border: `2px solid ${
                              positioning === "Excellent Positioning"
                                ? "#26a65d"
                                : positioning === "Average Positioning"
                                ? "#ED9E0E"
                                : "#f23645"
                            }`,
                            minWidth: width < 768 ? "100px" : "120px",
                                maxWidth: width < 768 ? "140px" : "160px",
                            textAlign: "center",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "8px",
                              fontWeight: "600",
                              letterSpacing: "0.8px",
                              color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                              margin: "0 0 2px 0",
                            }}
                          >
                            POSITIONING
                          </p>
                          <p
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              letterSpacing: "0.3px",
                              margin: 0,
                              color:
                                positioning === "Excellent Positioning"
                                  ? "#26a65d"
                                  : positioning === "Average Positioning"
                                  ? "#ED9E0E"
                                  : "#f23645",
                            }}
                          >
                            {positioning.replace(" Positioning", "").toUpperCase()}
                          </p>
                        </div>
                      )}
                      {sequence && (
                        <div
                          style={{
                            position: "relative",
                            padding: "4px 10px",
                            borderRadius: "12px",
                            backgroundColor:
                              sequence === "Excellent Sequence"
                                ? theme[0] === "dark"
                                  ? "rgba(38, 166, 93, 0.15)"
                                  : "rgba(38, 166, 93, 0.1)"
                                : sequence === "Average Sequence"
                                ? theme[0] === "dark"
                                  ? "rgba(237, 158, 14, 0.15)"
                                  : "rgba(237, 158, 14, 0.1)"
                                : theme[0] === "dark"
                                ? "rgba(242, 54, 69, 0.15)"
                                : "rgba(242, 54, 69, 0.1)",
                            border: `2px solid ${
                              sequence === "Excellent Sequence"
                                ? "#26a65d"
                                : sequence === "Average Sequence"
                                ? "#ED9E0E"
                                : "#f23645"
                            }`,
                            minWidth: width < 768 ? "100px" : "120px",
                                maxWidth: width < 768 ? "140px" : "160px",
                            textAlign: "center",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "8px",
                              fontWeight: "600",
                              letterSpacing: "0.8px",
                              color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                              margin: "0 0 2px 0",
                            }}
                          >
                            SEQUENCE
                          </p>
                          <p
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              letterSpacing: "0.3px",
                              margin: 0,
                              color:
                                sequence === "Excellent Sequence"
                                  ? "#26a65d"
                                  : sequence === "Average Sequence"
                                  ? "#ED9E0E"
                                  : "#f23645",
                            }}
                          >
                            {sequence.replace(" Sequence", "").toUpperCase()}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  {(() => {
                    const riskDiffers = forecastGrade && tradeGrade && forecastGrade !== tradeGrade;
                    
                    if (riskDiffers) {
                      return (
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "center" }}>
                          {forecastGrade && (
                            <div
                              style={{
                                position: "relative",
                                padding: "4px 10px",
                                borderRadius: "12px",
                                backgroundColor:
                                  forecastGrade === "Low Risk"
                                    ? theme[0] === "dark"
                                      ? "rgba(38, 166, 93, 0.15)"
                                      : "rgba(38, 166, 93, 0.1)"
                                    : forecastGrade === "High Risk"
                                      ? theme[0] === "dark"
                                        ? "rgba(255, 165, 0, 0.15)"
                                        : "rgba(255, 165, 0, 0.1)"
                                    : forecastGrade === "Invalid"
                                      ? theme[0] === "dark"
                                        ? "rgba(242, 54, 69, 0.15)"
                                        : "rgba(242, 54, 69, 0.1)"
                                      : theme[0] === "dark"
                                        ? "rgba(242, 54, 69, 0.15)"
                                        : "rgba(242, 54, 69, 0.1)",
                                border: `2px solid ${
                                  forecastGrade === "Low Risk"
                                    ? "#26a65d"
                                    : forecastGrade === "High Risk"
                                      ? "#FFA500"
                                      : forecastGrade === "Invalid"
                                        ? "#f23645"
                                        : "#f23645"
                                }`,
                                minWidth: width < 768 ? "100px" : "120px",
                                maxWidth: width < 768 ? "140px" : "160px",
                                textAlign: "center",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "8px",
                                  fontWeight: "600",
                                  letterSpacing: "0.8px",
                                  color:
                                    theme[0] !== "dark"
                                      ? "#6b7280"
                                      : "#9ca3af",
                                  margin: "0 0 2px 0",
                                }}
                              >
                                FORECAST
                              </p>
                              <p
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  letterSpacing: "0.3px",
                                  margin: 0,
                                  color:
                                    forecastGrade === "Low Risk"
                                      ? "#26a65d"
                                      : forecastGrade === "High Risk"
                                        ? "#FFA500"
                                        : forecastGrade === "Invalid"
                                          ? "#f23645"
                                          : "#f23645",
                                }}
                              >
                                {forecastGrade
                                  .replace(" Risk", "")
                                  .toUpperCase()}
                              </p>
                            </div>
                          )}
                          {tradeGrade && (
                            <div
                              style={{
                                position: "relative",
                                padding: "4px 10px",
                                borderRadius: "12px",
                                backgroundColor:
                                  tradeGrade === "Low Risk"
                                    ? theme[0] === "dark"
                                      ? "rgba(38, 166, 93, 0.15)"
                                      : "rgba(38, 166, 93, 0.1)"
                                    : tradeGrade === "High Risk"
                                      ? theme[0] === "dark"
                                        ? "rgba(255, 165, 0, 0.15)"
                                        : "rgba(255, 165, 0, 0.1)"
                                    : tradeGrade === "Invalid"
                                      ? theme[0] === "dark"
                                        ? "rgba(242, 54, 69, 0.15)"
                                        : "rgba(242, 54, 69, 0.1)"
                                      : theme[0] === "dark"
                                        ? "rgba(242, 54, 69, 0.15)"
                                        : "rgba(242, 54, 69, 0.1)",
                                border: `2px solid ${
                                  tradeGrade === "Low Risk"
                                    ? "#26a65d"
                                    : tradeGrade === "High Risk"
                                      ? "#FFA500"
                                      : tradeGrade === "Invalid"
                                        ? "#f23645"
                                        : "#f23645"
                                }`,
                                minWidth: width < 768 ? "100px" : "120px",
                                maxWidth: width < 768 ? "140px" : "160px",
                                textAlign: "center",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "8px",
                                  fontWeight: "600",
                                  letterSpacing: "0.8px",
                                  color:
                                    theme[0] !== "dark"
                                      ? "#6b7280"
                                      : "#9ca3af",
                                  margin: "0 0 2px 0",
                                }}
                              >
                                TRADE RISK
                              </p>
                              <p
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  letterSpacing: "0.3px",
                                  margin: 0,
                                  color:
                                    tradeGrade === "Low Risk"
                                      ? "#26a65d"
                                      : tradeGrade === "High Risk"
                                        ? "#FFA500"
                                        : tradeGrade === "Invalid"
                                          ? "#f23645"
                                          : "#f23645",
                                }}
                              >
                                {tradeGrade
                                  .replace(" Risk", "")
                                  .toUpperCase()}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    } else {
                      const grade = tradeGrade || forecastGrade;
                      return grade && (
                        <div
                          style={{
                            position: "relative",
                            padding: "4px 10px",
                            borderRadius: "12px",
                            backgroundColor:
                              grade === "Low Risk"
                                ? theme[0] === "dark"
                                  ? "rgba(38, 166, 93, 0.15)"
                                  : "rgba(38, 166, 93, 0.1)"
                                : grade === "High Risk"
                                ? theme[0] === "dark"
                                  ? "rgba(255, 165, 0, 0.15)"
                                  : "rgba(255, 165, 0, 0.1)"
                                : grade === "Invalid"
                                ? theme[0] === "dark"
                                  ? "rgba(242, 54, 69, 0.15)"
                                  : "rgba(242, 54, 69, 0.1)"
                                : theme[0] === "dark"
                                ? "rgba(242, 54, 69, 0.15)"
                                : "rgba(242, 54, 69, 0.1)",
                            border: `2px solid ${
                              grade === "Low Risk"
                                ? "#26a65d"
                                : grade === "High Risk"
                                ? "#FFA500"
                                : grade === "Invalid"
                                  ? "#f23645"
                                  : "#f23645"
                            }`,
                            minWidth: width < 768 ? "100px" : "120px",
                                maxWidth: width < 768 ? "140px" : "160px",
                            textAlign: "center",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "8px",
                              fontWeight: "600",
                              letterSpacing: "0.8px",
                              color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                              margin: "0 0 2px 0",
                            }}
                          >
                            RISK GRADE
                          </p>
                          <p
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              letterSpacing: "0.3px",
                              margin: 0,
                              color:
                                grade === "Low Risk"
                                  ? "#26a65d"
                                  : grade === "High Risk"
                                    ? "#FFA500"
                                    : grade === "Invalid"
                                      ? "#f23645"
                                      : "#f23645",
                            }}
                          >
                            {grade.replace(" Risk", "").toUpperCase()}
                          </p>
                        </div>
                      );
                    }
                  })()}
                </div>
              )}
            </div>

            {urls.length > 0 && (
              <div style={{ marginTop: "20px" }}>
                {/* Chart Screenshots */}
                {!accordion && (
                  <Grid container spacing={0} sx={{ maxWidth: "95%", margin: "0 auto" }}>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ padding: { xs: "0", md: "8px" } }}
                    >
                      <div
                        style={{
                          cursor: "pointer",
                          padding: "10px 16px",
                          margin: "0 auto 16px auto",
                          borderRadius: "12px",
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(45, 46, 51, 0.4)"
                              : "rgba(249, 250, 251, 0.8)",
                          border: `2px solid ${
                            theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                          }`,
                          textAlign: "center",
                          maxWidth: "160px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                            margin: 0,
                          }}
                        >
                          Daily Before
                        </p>
                      </div>
                      {daily_before ? (
                        <>
                          <img
                            style={{
                              width: "100%",
                              height: "430px",
                              objectFit: "cover",
                              borderRadius: "10px",
                              cursor: "pointer",
                            }}
                            src={daily_before}
                            alt={daily_before}
                            height="auto"
                            onClick={() => openImageViewer(6)}
                          />
                          <p
                            style={{
                              textAlign: "center",
                              fontSize: "16px",
                              fontWeight: "300",
                            }}
                          >
                            {entryTime || (date && moment(date, ["DD/MM/YY", "YYYY-MM-DD", moment.ISO_8601], true).format("HH:mm"))} - {date && moment(date, ["DD/MM/YY", "YYYY-MM-DD", moment.ISO_8601], true).format("DD/MM/YY")}
                          </p>
                        </>
                      ) : (
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "15px",
                            color: "#f23645",
                            fontWeight: "300",
                          }}
                        >
                          No Screenshot Detected
                        </p>
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ padding: { xs: "0", md: "8px" } }}
                    >
                      <div
                        style={{
                          cursor: "pointer",
                          padding: "10px 16px",
                          margin: "0 auto 16px auto",
                          borderRadius: "12px",
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(45, 46, 51, 0.4)"
                              : "rgba(249, 250, 251, 0.8)",
                          border: `2px solid ${
                            theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                          }`,
                          textAlign: "center",
                          maxWidth: "160px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                            margin: 0,
                          }}
                        >
                          1h After
                        </p>
                      </div>

                      {hour1_after ? (
                        <>
                          <img
                            style={{
                              width: "100%",
                              height: "430px",
                              objectFit: "cover",
                              borderRadius: "10px",
                              cursor: "pointer",
                            }}
                            src={hour1_after}
                            alt={hour1_after}
                            height="auto"
                            onClick={() => openImageViewer(3)}
                          />
                          <p
                            style={{
                              textAlign: "center",
                              fontSize: "16px",
                              fontWeight: "300",
                            }}
                          >
                            {exitTime || (exitDate && moment(exitDate, ["DD/MM/YY", "YYYY-MM-DD", moment.ISO_8601], true).format("HH:mm"))} - {exitDate ? moment(exitDate, ["DD/MM/YY", "YYYY-MM-DD", moment.ISO_8601], true).format("DD/MM/YY") : (date && moment(date, ["DD/MM/YY", "YYYY-MM-DD", moment.ISO_8601], true).format("DD/MM/YY"))}
                          </p>
                        </>
                      ) : (
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "15px",
                            color: "#f23645",
                            fontWeight: "300",
                          }}
                        >
                          No Screenshot Detected
                        </p>
                      )}
                    </Grid>
                  </Grid>
                )}
                
                <Accordion
                  expanded={accordion}
                  onChange={() => {}}
                  elevation={0}
                  sx={{
                    backgroundColor:
                      theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                    color: theme[0] !== "dark" ? "#374151" : "#e5e7eb",
                    borderRadius: "8px",
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <AccordionSummary 
                    aria-controls="panel1a-content" 
                    id="panel1a-header"
                    sx={{ display: "none" }}
                  >
                  </AccordionSummary>
                  <AccordionDetails
                    style={{
                      display: "flex",
                      justifyContent: "space-evenly",
                      flexWrap: "wrap",
                      padding: "0",
                    }}
                  >
                    <Grid container spacing={0} sx={{ maxWidth: "95%", margin: "0 auto", padding: "0" }}>
                      {urls.map((url, index) => {
                    const allUrls = [
                      min5_before,
                      min5_after,
                      min15_before,
                      min15_after,
                      hour1_before,
                      hour1_after,
                      hour4_before,
                      hour4_after,
                      daily_before,
                      daily_after,
                      weekly_before,
                      weekly_after,
                      dxyHourlyBefore,
                      dxyHourlyAfter,
                      dxyDailyBefore,
                      dxyDailyAfter,
                    ];
                    
                    const notes = [
                      min5_beforeNote,
                      min5_afterNote,
                      min15_beforeNote,
                      min15_afterNote,
                      oneHour_beforeNote,
                      oneHour_afterNote,
                      fourHour_beforeNote,
                      fourHour_afterNote,
                      daily_beforeNote,
                      daily_afterNote,
                      weekly_beforeNote,
                      weekly_afterNote,
                      dxyHourlyBeforeNote,
                      dxyHourlyAfterNote,
                      dxyDailyBeforeNote,
                      dxyDailyAfterNote,
                    ];
                    
                    const titles = [
                      "5m Before",
                      "5m After",
                      "15m Before",
                      "15m After",
                      "1h Before",
                      "1h After",
                      "4h Before",
                      "4h After",
                      "Daily Before",
                      "Daily After",
                      "Weekly Before",
                      "Weekly After",
                      "DXY LTF Before",
                      "DXY LTF After",
                      "DXY HTF Before",
                      "DXY HTF After",
                    ];
                    
                    const originalIndex = allUrls.indexOf(url);
                    const note = notes[originalIndex];
                    const title = titles[originalIndex];
                    
                    return (
                      <Grid key={index} item xs={12} md={6} sx={{ padding: { xs: "0", md: "8px" }, marginBottom: "20px" }}>
                        <div
                          style={{
                            cursor: "pointer",
                            padding: "10px 16px",
                            margin: "0 auto 16px auto",
                            borderRadius: "12px",
                            border: `2px solid ${
                              theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                            }`,
                            textAlign: "center",
                            maxWidth: "160px",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "16px",
                              fontWeight: "700",
                              color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                              margin: 0,
                            }}
                          >
                            {title}
                          </p>
                        </div>
                        <Tooltip title="Click to open screenshot" placement="top-end">
                          <img
                            style={imgStyle}
                            src={url}
                            alt={title}
                            height="auto"
                            onClick={() => openImageViewer(index)}
                          />
                        </Tooltip>
                        {note && (
                          <div
                            style={{
                              minHeight: "69px",
                              marginTop: "16px",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "15px",
                                lineHeight: "1.5",
                                color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                                margin: 0,
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {note}
                            </p>
                          </div>
                        )}
                      </Grid>
                    );
                  })}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </div>
            )}

            {/* View All Screenshots Button */}
            {urls.length > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "0 0 16px 0",
                }}
              >
                <div
                  onClick={(e) =>
                    setTimeout(() => {
                      setAccordion(accordion ? false : true);
                    }, 200)
                  }
                  style={{
                    padding: "10px 20px",
                    borderRadius: "24px",
                    cursor: "pointer",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(156, 163, 175, 0.12)"
                        : "rgba(107, 114, 128, 0.08)",
                    border: `2px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                    color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                    fontWeight: "600",
                    fontSize: "13px",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      theme[0] === "dark"
                        ? "rgba(156, 163, 175, 0.2)"
                        : "rgba(107, 114, 128, 0.15)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      theme[0] === "dark"
                        ? "rgba(156, 163, 175, 0.12)"
                        : "rgba(107, 114, 128, 0.08)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {!accordion ? "View All Screenshots" : "Close"}
                </div>
              </div>
            )}

            {/* Journal Questions Section */}
            <div>
              {(pros || executionRight || executionDeviate || cons || systemAppropriate || systemWhy || emotions || emotionsBefore || emotionsWhile || emotionsAfter || takeAgain || plan || extraNotes) && (
                <>
              {pros && (
                <div
                  style={{
                    width: "100%",
                    maxWidth: width <= 600 ? "100%" : "91.67%",
                    margin: "24px auto 16px auto",
                    padding: width <= 600 ? "20px" : "24px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                        }}
                      >
                        QUESTION 1
                      </p>
                      <p
                        style={{
                          fontSize: "17px",
                          fontWeight: "600",
                          margin: "0 0 4px 0",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        {questions.question_1}
                      </p>
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: "16px",
                      lineHeight: "1.6",
                      color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                      margin: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {pros}
                  </p>
                  {useNewQuestions && (
                    <>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                          margin: "16px 0 4px 0",
                        }}
                      >
                        Followed trading plan:
                      </p>
                      <p
                        style={{
                          fontSize: "16px",
                          lineHeight: "1.6",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                          margin: 0,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {executionRight}
                      </p>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                          margin: "12px 0 4px 0",
                        }}
                      >
                        Where did I deviate:
                      </p>
                      <p
                        style={{
                          fontSize: "16px",
                          lineHeight: "1.6",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                          margin: 0,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {executionDeviate}
                      </p>
                    </>
                  )}
                </div>
              )}

              {cons && (
                <div
                  style={{
                    width: "100%",
                    maxWidth: width <= 600 ? "100%" : "91.67%",
                    margin: "0 auto 16px auto",
                    padding: width <= 600 ? "20px" : "24px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                        }}
                      >
                        QUESTION 2
                      </p>
                      <p
                        style={{
                          fontSize: "17px",
                          fontWeight: "600",
                          margin: "0 0 4px 0",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        {questions.question_2}
                      </p>
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: "16px",
                      lineHeight: "1.6",
                      color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                      margin: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {cons}
                  </p>
                  {useNewQuestions && (
                    <>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                          margin: "16px 0 4px 0",
                        }}
                      >
                        System appropriate:
                      </p>
                      <p
                        style={{
                          fontSize: "16px",
                          lineHeight: "1.6",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                          margin: 0,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {systemAppropriate}
                      </p>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                          margin: "12px 0 4px 0",
                        }}
                      >
                        Why:
                      </p>
                      <p
                        style={{
                          fontSize: "16px",
                          lineHeight: "1.6",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                          margin: 0,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {systemWhy}
                      </p>
                    </>
                  )}
                </div>
              )}

              {emotions && (
                <div
                  style={{
                    width: "100%",
                    maxWidth: width <= 600 ? "100%" : "91.67%",
                    margin: "0 auto 16px auto",
                    padding: width <= 600 ? "20px" : "24px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                        }}
                      >
                        QUESTION 3
                      </p>
                      <p
                        style={{
                          fontSize: "17px",
                          fontWeight: "600",
                          margin: "0 0 4px 0",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        {questions.question_3}
                      </p>
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: "16px",
                      lineHeight: "1.6",
                      color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                      margin: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {emotions}
                  </p>
                  {useNewQuestions && (
                    <>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                          margin: "16px 0 4px 0",
                        }}
                      >
                        Before entry:
                      </p>
                      <p
                        style={{
                          fontSize: "16px",
                          lineHeight: "1.6",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                          margin: 0,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {emotionsBefore}
                      </p>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                          margin: "12px 0 4px 0",
                        }}
                      >
                        While active:
                      </p>
                      <p
                        style={{
                          fontSize: "16px",
                          lineHeight: "1.6",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                          margin: 0,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {emotionsWhile}
                      </p>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                          margin: "12px 0 4px 0",
                        }}
                      >
                        After exit:
                      </p>
                      <p
                        style={{
                          fontSize: "16px",
                          lineHeight: "1.6",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                          margin: 0,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {emotionsAfter}
                      </p>
                    </>
                  )}
                </div>
              )}

              {takeAgain && (
                <div
                  style={{
                    width: "100%",
                    maxWidth: width <= 600 ? "100%" : "91.67%",
                    margin: "0 auto 16px auto",
                    padding: width <= 600 ? "20px" : "24px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                        }}
                      >
                        QUESTION 4
                      </p>
                      <p
                        style={{
                          fontSize: "17px",
                          fontWeight: "600",
                          margin: "0 0 4px 0",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        {questions.question_4}
                      </p>
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: "16px",
                      lineHeight: "1.6",
                      color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                      margin: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {takeAgain}
                  </p>
                </div>
              )}

              {plan && !useNewQuestions && (
                <div
                  style={{
                    width: "100%",
                    maxWidth: width <= 600 ? "100%" : "91.67%",
                    margin: "0 auto 16px auto",
                    padding: width <= 600 ? "20px" : "24px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                        }}
                      >
                        QUESTION 5
                      </p>
                      <p
                        style={{
                          fontSize: "17px",
                          fontWeight: "600",
                          margin: "0 0 4px 0",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        {questions.question_5}
                      </p>
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: "16px",
                      lineHeight: "1.6",
                      color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                      margin: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {plan}
                  </p>
                </div>
              )}

              {extraNotes && !useNewQuestions && (
                <div
                  style={{
                    width: "100%",
                    maxWidth: width <= 600 ? "100%" : "91.67%",
                    margin: "0 auto 16px auto",
                    padding: width <= 600 ? "20px" : "24px",
                    borderRadius: "12px",
                    backgroundColor:
                      theme[0] === "dark"
                        ? "rgba(45, 46, 51, 0.4)"
                        : "rgba(249, 250, 251, 0.8)",
                    border: `1px solid ${
                      theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                    }`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                        }}
                      >
                        QUESTION 6
                      </p>
                      <p
                        style={{
                          fontSize: "17px",
                          fontWeight: "600",
                          margin: "0 0 4px 0",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        {questions.question_6}
                      </p>
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: "16px",
                      lineHeight: "1.6",
                      color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                      margin: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {extraNotes}
                  </p>
                </div>
              )}
                </>
              )}
            </div>

                      </div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SharedJournal;
